'use client';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as React from 'react';

import { cn } from '@/utils/cn';

type orientationProps = 'horizontal' | 'vertical' | undefined;
const ScrollBar = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>, React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & { colorScroll?: string }>(({ className, orientation = 'vertical', colorScroll = 'bg-zinc-800', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar ref={ref} orientation={orientation} className={cn('flex touch-none select-none transition-colors', orientation === 'vertical' && 'h-full w-2.5 border-s border-s-transparent p-[1px]', orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent bg-[#2B2E35] p-[1px]', className)} {...props}>
    <ScrollAreaPrimitive.ScrollAreaThumb className={cn('relative flex-1 rounded-full', colorScroll)} />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & { colorScroll?: string } & { orientation?: orientationProps } & { scrollbarClassName?: string } & { focusableViewport?: boolean }>(({ className, children, colorScroll, orientation, scrollbarClassName, focusableViewport, ...props }, ref) => (
  <ScrollAreaPrimitive.Root ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
    {/* focusableViewport: opt-in tabIndex so scrollable content is keyboard-reachable
        (WCAG). Off by default so non-scrolling ScrollAreas don't add empty tab stops.
        The ring therefore only ever shows on the ones that opt in. */}
    <ScrollAreaPrimitive.Viewport tabIndex={focusableViewport ? 0 : undefined} className='size-full rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--np-green)]'>
      {children}
    </ScrollAreaPrimitive.Viewport>
    <ScrollBar colorScroll={colorScroll} orientation={orientation} className={scrollbarClassName} />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

export { ScrollArea, ScrollBar };
